import { LinkBehavior } from '@components/link-behavior';
import { LinkProps } from '@mui/material/Link';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    font: Palette['primary'];
    border: Palette['primary'];
    bg: Palette['primary'];
  }
  interface PaletteOptions {
    font: PaletteOptions['primary'];
    border: PaletteOptions['primary'];
    bg: PaletteOptions['primary'];
    black: PaletteOptions['primary'];
    brand: PaletteOptions['primary'];
    brown: PaletteOptions['primary'];
    ece: PaletteOptions['primary'];
    gce: PaletteOptions['primary'];
    pending: PaletteOptions['primary'];
    trential: PaletteOptions['primary'];
    white: PaletteOptions['primary'];
    secondaryAccent: PaletteOptions['primary'];
  }
  interface BreakpointOverrides {
    bsm: true;
    lsm: true;
    bmd: true;
    lmd: true;
    blg: true;
    sxl: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true; // Add 'white' as a valid color
  }
}

export const palette = {
  primary: {
    light: '#E4F1FF',
    main: '#044DA1',
    // dark: will be calculated from palette.primary.main,
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#FAA42B',
    // dark: will be calculated from palette.secondary.main,
    contrastText: '#ffcc00',
    dark: '#A15700',
    light: '#FFEFDC'
  },
  secondaryAccent: {
    main: '#32858A',
    light: '#f4fcfd'
  },
  error: {
    main: '#C84646',
    dark: '#782A2A',
    medium: '#F8DADA',
    light: '#F9ECEC'
  },
  // warning: {},
  // info: {},
  success: {
    main: '#239D56',
    light: '#DEF6E7',
    lighter: '#E9F5EE'
  },
  font: {
    dark: '#404052',
    main: '#515151',
    medium: '#66747F',
    light: '#CFCFD1',
    disabled: '#8F8F92'
  },
  border: {
    main: '#DADADA',
    dark: '#79747e',
    light: '#F2F7FF',
    lighter: '#E7EDF6'
  },
  bg: {
    main: '#FAFAFA',
    dark: '#E7EDF6',
    light: '#F6F9FC',
    darker: '',
    lighter: '#F5F5F5',
    white: '#fff',
    disabled: '#EFEFEF',
    darkDisabled: '#797886'
  },
  white: {
    main: '#ffffff',
    light: '#ffffff',
    contrastText: '#000000'
  },
  black: {
    main: '#000000',
    light: '#ffffff',
    contrastText: '#fff'
    // light: will be calculated from palette.primary.main,
    // contrastText: will be calculated to contrast with palette.primary.main
  },
  brand: {
    main: '#044DA1',
    light: '#ffffff',
    contrastText: '#fff'
    // light: will be calculated from palette.primary.main,
    // contrastText: will be calculated to contrast with palette.primary.main
  },
  pending: {
    main: '#FFA800',
    light: '#FEF8EC',
    contrastBackground: '#ffe2ba'
  },
  // warning: {
  //   main: '#F8A908',
  //   light: '#DADADA',
  //   contrastText: '#fff'
  // },
  // signifies neutral
  brown: {
    main: '#824700',
    light: '#fffaf4',
    contrastText: '#ffffff'
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
  trential: {
    main: '#037FFF',
    light: '#037FFF',
    contrastText: '#fff'
    // light: will be calculated from palette.primary.main,
    // contrastText: will be calculated to contrast with palette.primary.main
  },
  ece: {
    main: '#FB9200',
    light: '#ffd9a8',
    contrastText: '#fff'
    // light: will be calculated from palette.primary.main,
    // contrastText: will be calculated to contrast with palette.primary.main
  },
  gce: {
    main: '##14527F',
    light: '#ffd9a8',
    contrastText: '#fff'
    // light: will be calculated from palette.primary.main,
    // contrastText: will be calculated to contrast with palette.primary.main
  }
};

const theme = createTheme({
  components: {
    MuiCard: {
      styleOverrides: {
        root: () => ({
          borderRadius: '14px'
        })
      }
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior
      } as LinkProps
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior
      },
      styleOverrides: {
        // Name of the slot
        root: () => ({
          borderRadius: '8px',
          padding: '1em 2em 1em 2em'
        })
      }
    },
    MuiButton: {
      styleOverrides: {
        // Name of the slot
        root: () => ({
          fontSize: '1.4rem',
          textTransform: 'initial',
          borderRadius: '8px',
          padding: '1em 2em 1em 2em',
          width: '100%',
          fontWeight: 700,
          fontFamily: `"Source Sans 3", "Lato", Sans-serif`
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'Source Sans 3',
          fontSize: '1.2rem'
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        PaperProps: {
          sx: {
            borderRadius: '8px'
          }
        }
      }
    }
  },
  palette,
  typography: {
    fontFamily: 'Source Sans 3'
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spacing: (factor: any) => `${factor}rem`,
  breakpoints: {
    values: {
      xs: 0,
      sm: 750,
      bsm: 700,
      lsm: 800,
      md: 900,
      bmd: 1000,
      lmd: 1100,
      lg: 1200,
      blg: 1300,
      sxl: 1400,
      xl: 1630
    }
  }
});

theme.typography.button = {
  textTransform: 'none'
};

theme.typography.h1 = {
  color: '#404052'
};
theme.typography.h2 = {
  color: '#404052'
};
theme.typography.h3 = {
  color: '#404052',
  fontSize: '2.2rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.6rem'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '2rem'
  }
};
theme.typography.h4 = {
  color: '#404052',
  fontSize: '2rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.44rem'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.8rem'
  }
};

theme.typography.h5 = {
  color: '#404052',
  fontSize: '1.8rem',
  fontWeight: 600,
  lineHeight: '2rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.24rem',
    lineHeight: '1.5rem'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.4rem',
    lineHeight: '2rem'
  }
};

theme.typography.h6 = {
  color: '#404052',
  fontSize: '1.6rem',
  fontWeight: 600,
  lineHeight: '2.4rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px',
    lineHeight: '1.5rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '16px',
    lineHeight: '2rem'
  }
};

theme.typography.body1 = {
  fontSize: '14px',
  color: '#404052',
  lineHeight: '1.6rem'
};

theme.typography.body2 = {
  fontSize: '12px',
  color: '#222222',
  lineHeight: '1.6rem',
  fontWeight: '400'
};

theme.typography.caption = {
  fontSize: '1.1rem',
  color: '#404052'
  // margin: '0.8em 0'
};

theme.typography.body1M = {
  ...theme.typography.body1,
  margin: '0.8em 0'
};

theme.typography.body2M = {
  ...theme.typography.body2,
  margin: '0.8em 0'
};

theme.typography.captionM = {
  ...theme.typography.caption,
  margin: '0.8em 0'
};

export default theme;
